import { Suspense, lazy } from 'react';
import { Navigate, useRoutes } from 'react-router-dom';
import TeamWorkspaceGate from './components/team/TeamWorkspaceGate';
import PageTransition from './components/PageTransition';
import ProtectedRoute from './components/ProtectedRoute';
import ProtectedAdminLayout from './components/admin/ProtectedAdminLayout';
import ProtectedDoctorLayout from './components/doctor/ProtectedDoctorLayout';
import ApproverProtectedRoute from './components/ApproverProtectedRoute';
import CommentModeratorProtectedRoute from './components/CommentModeratorProtectedRoute';
import DoctorProfileApprovedRoute from './components/doctor/DoctorProfileApprovedRoute';
import { useAuth } from './contexts/AuthContext';

// Lazy load heavy components for better code splitting
const LandingPage = lazy(() => import('./components/landingPage'));
const Layout = lazy(() => import('./layout'));
const Login = lazy(() => import('./auth/Login'));
const UniversalSignup = lazy(
  () => import('./auth/UniversalSignup/UniversalSignup'),
);
const ForgotPassword = lazy(() => import('./auth/ForgotPassword'));
const ResetPassword = lazy(() => import('./auth/ResetPassword'));
const ClaimAccount = lazy(() => import('./auth/ClaimAccount'));
const RecipePage = lazy(
  () => import('./components/landingPage/HeaderPages/RecipePage'),
);
const RecipeDetailPage = lazy(
  () => import('./components/landingPage/HeaderPages/RecipeDetailPage'),
);
const PublicDoctorProfilePage = lazy(
  () => import('./components/landingPage/HeaderPages/PublicDoctorProfilePage'),
);
const DoctorsDirectoryPage = lazy(
  () => import('./components/landingPage/HeaderPages/DoctorsDirectoryPage'),
);
const LikedRecipesPage = lazy(
  () => import('./components/landingPage/HeaderPages/LikedRecipesPage'),
);
const TriedRemediesPage = lazy(
  () => import('./components/landingPage/HeaderPages/TriedRemediesPage'),
);
const SavedRecipesPage = lazy(
  () => import('./components/landingPage/HeaderPages/SavedRecipesPage'),
);
const CollectionDetailPage = lazy(
  () => import('./components/landingPage/HeaderPages/CollectionDetailPage'),
);
const Profile = lazy(() => import('./components/Profile'));
const NotificationsPage = lazy(() => import('./components/NotificationsPage'));
const FamilyHubLayout = lazy(
  () => import('./components/family/FamilyHubLayout'),
);
const FamilyMyProfilePage = lazy(
  () => import('./components/family/FamilyMyProfilePage'),
);
const FamilyMembersOnlyPage = lazy(
  () => import('./components/family/FamilyMembersOnlyPage'),
);
const FamilySharedWithMePage = lazy(
  () => import('./components/family/FamilySharedWithMePage'),
);
const FamilyMemberDetailsPage = lazy(
  () => import('./components/family/FamilyMemberDetailsPage'),
);
const InviteAcceptPage = lazy(
  () => import('./components/family/InviteAcceptPage'),
);
const ShareInviteAcceptPage = lazy(
  () => import('./components/family/ShareInviteAcceptPage'),
);
const MyTeamPage = lazy(() => import('./components/team/MyTeamPage'));
const TeamEditRecipe = lazy(
  () => import('./components/doctor/pages/TeamEditRecipe'),
);
const TeamInviteAcceptPage = lazy(
  () => import('./components/team/TeamInviteAcceptPage'),
);

const DoctorDashboard = lazy(
  () => import('./components/doctor/DoctorDashboard'),
);
const DoctorProfile = lazy(
  () => import('./components/doctor/pages/DoctorProfile'),
);
const DoctorAppointments = lazy(
  () => import('./components/doctor/pages/DoctorAppointments'),
);
const DoctorRecipe = lazy(
  () => import('./components/doctor/pages/DoctorRecipe'),
);
const ViewRecipe = lazy(() => import('./components/doctor/pages/ViewRecipe'));
const EditRecipe = lazy(() => import('./components/doctor/pages/EditRecipe'));
const DoctorSettings = lazy(
  () => import('./components/doctor/pages/DoctorSettings'),
);
const AdminDashboard = lazy(() => import('./components/admin/AdminDashboard'));
const AdminSearch = lazy(() => import('./components/admin/pages/AdminSearch'));
const AdminRecipe = lazy(() => import('./components/admin/pages/AdminRecipe'));
const AdminReviews = lazy(
  () => import('./components/admin/pages/AdminReviews'),
);
const AdminRecipeDetail = lazy(
  () => import('./components/admin/pages/AdminRecipeDetail'),
);
const AdminRecipeDataTraces = lazy(
  () => import('./components/admin/pages/AdminRecipeDataTraces'),
);

const AdminUser = lazy(() => import('./components/admin/pages/AdminUser'));
const AdminDoctor = lazy(() => import('./components/admin/pages/AdminDoctor'));
const AdminDoctorDetail = lazy(
  () => import('./components/admin/pages/AdminDoctorDetail'),
);
const AdminSettings = lazy(
  () => import('./components/admin/pages/AdminSettings'),
);
const AdminImpersonationSettings = lazy(
  () => import('./components/admin/pages/AdminImpersonationSettings'),
);
const AdminUserReviewComments = lazy(
  () => import('./components/admin/pages/AdminUserReviewComments'),
);
const ManageLists = lazy(
  () => import('./components/admin/pages/ManageLists/ManageLists'),
);
const ManageListPage = lazy(
  () => import('./components/admin/pages/ManageLists/ManageListPage'),
);

const ApproverQueuePage = lazy(
  () => import('./components/approver/ApproverQueuePage'),
);
const DoctorReviewQueuePage = lazy(
  () => import('./components/doctor/DoctorReviewQueuePage'),
);
const DoctorReviewDetail = lazy(
  () => import('./components/doctor/pages/DoctorReviewDetail'),
);
const DoshaTest = lazy(() => import('./components/DoshaTest/DoshaTest'));
const AICook = lazy(() => import('./components/ai-assist/AICook'));

const Search = lazy(() => import('./components/Search'));

const RouteLoader = () => (
  <div className="min-h-screen bg-gradient-to-br from-[#F8F7EC] to-[#E8F1D1] flex items-center justify-center">
    <div className="text-center">
      <div className="w-16 h-16 border-4 border-[#7E8D4C] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
      <p className="text-[#6b6358]">Loading...</p>
    </div>
  </div>
);

/** Chunk-load fallback for UniversalSignup — matches page background while Layout supplies Navbar/Footer. */
const SignupRouteFallback = () => (
  <div className="min-h-screen bg-[#F9F8F4] font-sans text-gray-900 flex flex-col">
    <div className="flex-1 flex flex-col items-center justify-center pt-16 pb-16 px-4">
      <div className="flex flex-col items-center gap-3 text-[#7E8D4C]">
        <div className="w-8 h-8 border-2 border-[#7E8D4C] border-t-transparent rounded-full animate-spin" />
        <span className="text-sm font-medium text-gray-500">Loading…</span>
      </div>
    </div>
  </div>
);

// Smart catch-all route that waits for auth to load before redirecting
const NotFoundRedirect = () => {
  const { isLoading, isAuthenticated, user, getRoleBasedRedirect } = useAuth();

  // Wait for auth to finish loading
  if (isLoading) {
    return <RouteLoader />;
  }

  // If authenticated, redirect to their appropriate dashboard
  if (isAuthenticated && user) {
    const redirectPath = getRoleBasedRedirect(
      user.role,
      user.active_profile_type,
    );
    return <Navigate to={redirectPath} replace />;
  }

  // If not authenticated, redirect to landing page
  return <Navigate to="/" replace />;
};

// Define routes inside component to ensure they're created after providers are set up
const AppRoutes = () => {
  const routes = [
    {
      element: (
        <Suspense fallback={<RouteLoader />}>
          <Layout />
        </Suspense>
      ),
      children: [
        {
          path: '/',
          element: (
            <Suspense fallback={<RouteLoader />}>
              <LandingPage />
            </Suspense>
          ),
        },
        {
          path: '/signup',
          element: (
            <Suspense fallback={<SignupRouteFallback />}>
              <UniversalSignup />
            </Suspense>
          ),
        },
        {
          path: '/remedy',
          element: (
            <Suspense fallback={<RouteLoader />}>
              <RecipePage />
            </Suspense>
          ),
        },
        {
          path: '/remedy/:id',
          element: (
            <Suspense fallback={<RouteLoader />}>
              <RecipeDetailPage />
            </Suspense>
          ),
        },
        {
          path: '/doctors',
          element: (
            <Suspense fallback={<RouteLoader />}>
              <DoctorsDirectoryPage />
            </Suspense>
          ),
        },
        {
          path: '/doctors/:userId',
          element: (
            <Suspense fallback={<RouteLoader />}>
              <PublicDoctorProfilePage />
            </Suspense>
          ),
        },
        {
          path: '/liked',
          element: (
            <Suspense fallback={<RouteLoader />}>
              <LikedRecipesPage />
            </Suspense>
          ),
        },
        {
          path: '/tried',
          element: (
            <Suspense fallback={<RouteLoader />}>
              <TriedRemediesPage />
            </Suspense>
          ),
        },
        {
          path: '/saved',
          element: (
            <Suspense fallback={<RouteLoader />}>
              <SavedRecipesPage />
            </Suspense>
          ),
        },
        {
          path: '/collection/:id',
          element: (
            <Suspense fallback={<RouteLoader />}>
              <CollectionDetailPage />
            </Suspense>
          ),
        },
        {
          path: '/search',
          element: (
            <Suspense fallback={<RouteLoader />}>
              <Search />
            </Suspense>
          ),
        },
        {
          path: '/profile',
          element: (
            <ProtectedRoute>
              <Suspense fallback={<RouteLoader />}>
                <Profile />
              </Suspense>
            </ProtectedRoute>
          ),
        },
        {
          path: '/notifications',
          element: (
            <ProtectedRoute>
              <Suspense fallback={<RouteLoader />}>
                <NotificationsPage />
              </Suspense>
            </ProtectedRoute>
          ),
        },
        {
          path: '/family',
          element: (
            <ProtectedRoute>
              <Suspense fallback={<RouteLoader />}>
                <FamilyHubLayout />
              </Suspense>
            </ProtectedRoute>
          ),
          children: [
            {
              index: true,
              element: <Navigate to="family-members" replace />,
            },
            {
              path: 'my-profile',
              element: (
                <Suspense fallback={<RouteLoader />}>
                  <FamilyMyProfilePage />
                </Suspense>
              ),
            },
            {
              path: 'family-members',
              element: (
                <Suspense fallback={<RouteLoader />}>
                  <FamilyMembersOnlyPage />
                </Suspense>
              ),
            },
            {
              // Use UUID in the URL (avoid exposing incremental numeric ids).
              path: 'family-members/:memberUuid',
              element: (
                <Suspense fallback={<RouteLoader />}>
                  <FamilyMemberDetailsPage />
                </Suspense>
              ),
            },
            {
              path: 'shared-with-me',
              element: (
                <Suspense fallback={<RouteLoader />}>
                  <FamilySharedWithMePage />
                </Suspense>
              ),
            },
          ],
        },
        {
          path: '/invite/accept',
          element: (
            <Suspense fallback={<RouteLoader />}>
              <InviteAcceptPage />
            </Suspense>
          ),
        },
        {
          path: '/family/share/accept',
          element: (
            <Suspense fallback={<RouteLoader />}>
              <ShareInviteAcceptPage />
            </Suspense>
          ),
        },
        {
          path: '/team',
          element: (
            <ProtectedRoute>
              <Suspense fallback={<RouteLoader />}>
                <MyTeamPage />
              </Suspense>
            </ProtectedRoute>
          ),
        },
        {
          path: '/team/invite',
          element: (
            <ProtectedRoute>
              <Navigate to="/team?tab=invites" replace />
            </ProtectedRoute>
          ),
        },
        {
          path: '/team/invite/accept',
          element: (
            <Suspense fallback={<RouteLoader />}>
              <TeamInviteAcceptPage />
            </Suspense>
          ),
        },
        {
          path: '/team/workspace',
          element: (
            <ProtectedRoute>
              <TeamWorkspaceGate />
            </ProtectedRoute>
          ),
          children: [
            {
              index: true,
              element: <Navigate to="remedies" replace />,
            },
            {
              path: 'remedies',
              element: (
                <Suspense fallback={<RouteLoader />}>
                  <DoctorRecipe teamWorkspace />
                </Suspense>
              ),
            },
            {
              path: 'remedies/create',
              element: (
                <Suspense fallback={<RouteLoader />}>
                  <DoctorRecipe viewMode="create" teamWorkspace />
                </Suspense>
              ),
            },
            {
              path: 'remedies/edit/:id',
              element: (
                <Suspense fallback={<RouteLoader />}>
                  <TeamEditRecipe />
                </Suspense>
              ),
            },
            {
              path: 'remedies/view/:id',
              element: (
                <Suspense fallback={<RouteLoader />}>
                  <ViewRecipe />
                </Suspense>
              ),
            },
          ],
        },
        {
          path: '/dosha-test',
          element: (
            <ProtectedRoute>
              <Suspense fallback={<RouteLoader />}>
                <DoshaTest />
              </Suspense>
            </ProtectedRoute>
          ),
        },
        {
          path: '/cook-assist',
          element: (
            <Suspense fallback={<RouteLoader />}>
              <AICook />
            </Suspense>
          ),
        },
        {
          path: '/review-comments',
          element: (
            <CommentModeratorProtectedRoute>
              <Suspense fallback={<RouteLoader />}>
                <AdminUserReviewComments />
              </Suspense>
            </CommentModeratorProtectedRoute>
          ),
        },
        // Approver Queue - accessible to any user with canApprove permission
        {
          path: '/approver-queue',
          element: (
            <ApproverProtectedRoute>
              <Suspense fallback={<RouteLoader />}>
                <ApproverQueuePage basePath="" />
              </Suspense>
            </ApproverProtectedRoute>
          ),
        },
      ],
    },
    {
      path: '/doctor',
      element: <ProtectedDoctorLayout />,
      children: [
        {
          path: 'dashboard',
          element: (
            <Suspense fallback={<RouteLoader />}>
              <DoctorDashboard />
            </Suspense>
          ),
        },
        {
          path: 'profile',
          element: (
            <Suspense fallback={<RouteLoader />}>
              <DoctorProfile />
            </Suspense>
          ),
        },
        {
          path: 'notifications',
          element: (
            <Suspense fallback={<RouteLoader />}>
              <NotificationsPage basePath="/doctor" />
            </Suspense>
          ),
        },
        {
          path: 'appointment',
          element: (
            <Suspense fallback={<RouteLoader />}>
              <DoctorAppointments />
            </Suspense>
          ),
        },

        {
          path: 'approver-queue',
          element: (
            <Suspense fallback={<RouteLoader />}>
              <ApproverQueuePage basePath="/doctor" />
            </Suspense>
          ),
        },
        {
          path: 'review-comments',
          element: (
            <CommentModeratorProtectedRoute>
              <Suspense fallback={<RouteLoader />}>
                <AdminUserReviewComments />
              </Suspense>
            </CommentModeratorProtectedRoute>
          ),
        },
        {
          path: 'review-queue',
          element: (
            <DoctorProfileApprovedRoute>
              <Suspense fallback={<RouteLoader />}>
                <DoctorReviewQueuePage />
              </Suspense>
            </DoctorProfileApprovedRoute>
          ),
        },
        {
          path: 'reviews/:id',
          element: (
            <DoctorProfileApprovedRoute>
              <Suspense fallback={<RouteLoader />}>
                <DoctorReviewDetail />
              </Suspense>
            </DoctorProfileApprovedRoute>
          ),
        },
        {
          path: 'remedy',
          element: (
            <Suspense fallback={<RouteLoader />}>
              <DoctorRecipe />
            </Suspense>
          ),
        },
        {
          path: 'remedy/create',
          element: (
            <Suspense fallback={<RouteLoader />}>
              <DoctorRecipe viewMode="create" />
            </Suspense>
          ),
        },
        {
          path: 'remedy/view/:id',
          element: (
            <Suspense fallback={<RouteLoader />}>
              <ViewRecipe />
            </Suspense>
          ),
        },
        {
          path: 'remedy/edit/:id',
          element: (
            <Suspense fallback={<RouteLoader />}>
              <EditRecipe />
            </Suspense>
          ),
        },
        {
          path: 'setting',
          element: (
            <Suspense fallback={<RouteLoader />}>
              <DoctorSettings />
            </Suspense>
          ),
        },
        {
          path: 'team/invite',
          element: <Navigate to="/doctor/team?tab=invites" replace />,
        },
        {
          path: 'team',
          element: (
            <Suspense fallback={<RouteLoader />}>
              <MyTeamPage />
            </Suspense>
          ),
        },
        {
          path: '',
          element: <Navigate to="/doctor/dashboard" replace />,
        },
      ],
    },
    {
      path: '/admin',
      element: <ProtectedAdminLayout />,
      children: [
        {
          path: 'dashboard',
          element: (
            <Suspense fallback={<RouteLoader />}>
              <AdminDashboard />
            </Suspense>
          ),
        },
        {
          path: 'search',
          element: (
            <Suspense fallback={<RouteLoader />}>
              <AdminSearch />
            </Suspense>
          ),
        },
        {
          path: 'remedy',
          element: (
            <Suspense fallback={<RouteLoader />}>
              <AdminRecipe />
            </Suspense>
          ),
        },
        {
          path: 'remedy-traces',
          element: (
            <Suspense fallback={<RouteLoader />}>
              <AdminRecipeDataTraces />
            </Suspense>
          ),
        },
        {
          path: 'approver-queue',
          element: (
            <Suspense fallback={<RouteLoader />}>
              <ApproverQueuePage basePath="/admin" />
            </Suspense>
          ),
        },
        {
          path: 'reviews',
          element: (
            <Suspense fallback={<RouteLoader />}>
              <AdminReviews />
            </Suspense>
          ),
        },
        {
          path: 'remedies/:recipeId',
          element: (
            <Suspense fallback={<RouteLoader />}>
              <AdminRecipeDetail />
            </Suspense>
          ),
        },
        {
          path: 'user',
          element: (
            <Suspense fallback={<RouteLoader />}>
              <AdminUser />
            </Suspense>
          ),
        },
        {
          path: 'doctor',
          element: (
            <Suspense fallback={<RouteLoader />}>
              <AdminDoctor />
            </Suspense>
          ),
        },
        {
          path: 'doctor/:doctorId',
          element: (
            <Suspense fallback={<RouteLoader />}>
              <AdminDoctorDetail />
            </Suspense>
          ),
        },
        {
          path: 'manage-list',
          element: (
            <Suspense fallback={<RouteLoader />}>
              <ManageLists />
            </Suspense>
          ),
        },
        {
          path: 'manage-list/:listName',
          element: (
            <Suspense fallback={<RouteLoader />}>
              <ManageListPage />
            </Suspense>
          ),
        },
        {
          path: 'setting',
          element: (
            <Suspense fallback={<RouteLoader />}>
              <AdminSettings />
            </Suspense>
          ),
        },
        {
          path: 'user-comments',
          element: (
            <Suspense fallback={<RouteLoader />}>
              <AdminUserReviewComments />
            </Suspense>
          ),
        },
        {
          path: 'impersonation-setting',
          element: (
            <Suspense fallback={<RouteLoader />}>
              <AdminImpersonationSettings />
            </Suspense>
          ),
        },
        {
          path: '',
          element: <Navigate to="/admin/dashboard" replace />,
        },
      ],
    },
    {
      path: '/login',
      element: (
        <Suspense fallback={<RouteLoader />}>
          <Login />
        </Suspense>
      ),
    },
    {
      path: '/forgot-password',
      element: (
        <Suspense fallback={<RouteLoader />}>
          <ForgotPassword />
        </Suspense>
      ),
    },
    {
      path: '/reset-password',
      element: (
        <Suspense fallback={<RouteLoader />}>
          <ResetPassword />
        </Suspense>
      ),
    },
    {
      path: '/claim',
      element: (
        <Suspense fallback={<RouteLoader />}>
          <ClaimAccount />
        </Suspense>
      ),
    },
    { path: '*', element: <NotFoundRedirect /> },
  ];

  const element = useRoutes(routes);
  return element ? <PageTransition>{element}</PageTransition> : element;
};

export default AppRoutes;
